/**
 * RRULE Helper Functions for Recurring Appointments
 * Handles building RRULE strings from UI inputs and expanding them into date arrays
 */

import { createLocalDateTime, formatLocalDate, formatLocalTime, addMinutes } from '../../utils/timezone';

// Day mapping for RRULE BYDAY values
const RRULE_DAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Build RRULE string from recurrence parameters
 * @param {Object} params - Recurrence parameters
 * @param {string} params.frequency - 'daily', 'weekly', 'monthly'
 * @param {number} params.interval - Repeat every N periods
 * @param {number[]} params.weeklyDays - Array of day indices (0=Sun, 6=Sat) for weekly
 * @param {string} params.monthlyMode - 'sameDate', 'sameWeekday', 'lastWeekday'
 * @param {Date} params.startDateTime - Start date/time
 * @param {Object} params.ends - End condition {type: 'count'|'date', value: number|Date}
 * @param {string} params.timezone - Timezone string
 * @returns {string} RRULE string
 */
export function buildRRule({ frequency, interval, weeklyDays = [], monthlyMode, startDateTime, ends, timezone }) {
  let rrule = `FREQ=${frequency.toUpperCase()}`;

  if (interval > 1) {
    rrule += `;INTERVAL=${interval}`;
  }

  // Handle weekly days
  if (frequency === 'weekly' && weeklyDays.length > 0) {
    const byDays = weeklyDays.map(day => RRULE_DAYS[day]).join(',');
    rrule += `;BYDAY=${byDays}`;
  }

  // Handle monthly modes
  if (frequency === 'monthly') {
    const startDate = new Date(startDateTime);

    if (monthlyMode === 'sameDate') {
      rrule += `;BYMONTHDAY=${startDate.getDate()}`;
    } else if (monthlyMode === 'sameWeekday') {
      const dayOfWeek = RRULE_DAYS[startDate.getDay()];
      const weekOfMonth = Math.ceil(startDate.getDate() / 7);
      rrule += `;BYDAY=${dayOfWeek};BYSETPOS=${weekOfMonth}`;
    } else if (monthlyMode === 'lastWeekday') {
      const dayOfWeek = RRULE_DAYS[startDate.getDay()];
      rrule += `;BYDAY=${dayOfWeek};BYSETPOS=-1`;
    }
  }

  // Handle end conditions
  if (ends.type === 'count') {
    rrule += `;COUNT=${ends.value}`;
  } else if (ends.type === 'date' && ends.value) {
    const untilDate = new Date(ends.value);
    if (!isNaN(untilDate.getTime())) {
      untilDate.setHours(23, 59, 59, 999);
      rrule += `;UNTIL=${untilDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
    }
  }

  return rrule;
}

/**
 * Expand RRULE into array of dates within a window
 * @param {Object} params - Expansion parameters
 * @param {string} params.rrule - RRULE string
 * @param {Date} params.start - Start date/time
 * @param {string} params.timezone - Timezone string
 * @param {Date} params.windowStart - Window start date
 * @param {Date} params.windowEnd - Window end date
 * @param {number} params.maxOccurrences - Maximum occurrences to return
 * @returns {Date[]} Array of occurrence dates
 */
export function expandRRule({ rrule, start, timezone, windowStart, windowEnd, maxOccurrences = 100 }) {
  const occurrences = [];
  const rules = parseRRule(rrule);

  let currentDate = new Date(start);
  let count = 0;
  const maxCount = rules.COUNT || maxOccurrences;
  const untilDate = rules.UNTIL ? new Date(rules.UNTIL) : null;

  // Safety guard
  let iterations = 0;
  const maxIterations = 2000;

  while (count < maxCount && iterations < maxIterations) {
    iterations++;

    // Check if we're past the until date
    if (untilDate && currentDate > untilDate) break;

    // Check if we're past the window end
    if (windowEnd && currentDate > windowEnd) break;

    let shouldInclude = false;

    if (rules.FREQ === 'DAILY') {
      shouldInclude = true;
    } else if (rules.FREQ === 'WEEKLY') {
      if (rules.BYDAY && rules.BYDAY.length > 0) {
        const dayOfWeek = RRULE_DAYS[currentDate.getDay()];
        shouldInclude = rules.BYDAY.includes(dayOfWeek);
      } else {
        shouldInclude = currentDate.getDay() === start.getDay();
      }
    } else if (rules.FREQ === 'MONTHLY') {
      shouldInclude = isMonthlyMatch(currentDate, start, rules);
    }

    if (shouldInclude && (!windowStart || currentDate >= windowStart)) {
      occurrences.push(new Date(currentDate));
      count++;
    }

    // Advance to next potential date
    currentDate = getNextDate(currentDate, rules, start);
  }

  return occurrences;
}

/**
 * Parse RRULE string into object
 * @param {string} rrule - RRULE string
 * @returns {Object} Parsed rule object
 */
function parseRRule(rrule) {
  const rules = {};
  const parts = rrule.split(';');

  parts.forEach(part => {
    const [key, value] = part.split('=');
    if (key === 'BYDAY') {
      rules[key] = value.split(',');
    } else if (key === 'COUNT' || key === 'INTERVAL' || key === 'BYMONTHDAY' || key === 'BYSETPOS') {
      rules[key] = parseInt(value);
    } else if (key === 'UNTIL') {
      rules[key] = value;
    } else {
      rules[key] = value;
    }
  });

  // Set defaults
  rules.INTERVAL = rules.INTERVAL || 1;

  return rules;
}

/**
 * Check if a date matches monthly recurrence rules
 * @param {Date} date - Date to check
 * @param {Date} start - Start date
 * @param {Object} rules - Parsed RRULE object
 * @returns {boolean} Whether date matches
 */
function isMonthlyMatch(date, start, rules) {
  if (rules.BYMONTHDAY) {
    // Same date each month, but handle month-end clamping
    const targetDay = rules.BYMONTHDAY;
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const actualDay = Math.min(targetDay, lastDayOfMonth);
    return date.getDate() === actualDay;
  }

  if (rules.BYDAY && rules.BYSETPOS !== undefined) {
    const targetDayOfWeek = rules.BYDAY[0];
    const targetDayIndex = RRULE_DAYS.indexOf(targetDayOfWeek);

    if (date.getDay() !== targetDayIndex) return false;

    if (rules.BYSETPOS === -1) {
      // Last occurrence of weekday in month
      const nextWeek = new Date(date);
      nextWeek.setDate(date.getDate() + 7);
      return nextWeek.getMonth() !== date.getMonth();
    } else {
      // Nth occurrence of weekday in month
      const weekOfMonth = Math.ceil(date.getDate() / 7);
      return weekOfMonth === rules.BYSETPOS;
    }
  }

  // Default: same date each month
  return date.getDate() === start.getDate();
}

/**
 * Get next potential date based on frequency
 * @param {Date} currentDate - Current date
 * @param {Object} rules - Parsed RRULE object
 * @param {Date} start - Start date
 * @returns {Date} Next date to check
 */
function getNextDate(currentDate, rules, start) {
  const nextDate = new Date(currentDate);

  if (rules.FREQ === 'DAILY') {
    nextDate.setDate(nextDate.getDate() + rules.INTERVAL);
  } else if (rules.FREQ === 'WEEKLY') {
    if (rules.BYDAY && rules.BYDAY.length > 1) {
      // Multiple days per week - advance by 1 day
      nextDate.setDate(nextDate.getDate() + 1);
    } else {
      // Single day per week - advance by interval weeks
      nextDate.setDate(nextDate.getDate() + (7 * rules.INTERVAL));
    }
  } else if (rules.FREQ === 'MONTHLY') {
    nextDate.setMonth(nextDate.getMonth() + rules.INTERVAL);
  }

  return nextDate;
}

/**
 * Get human-readable description of recurrence rule
 * @param {string} rrule - RRULE string
 * @param {Date} start - Start date
 * @returns {string} Human-readable description
 */
export function getRRuleDescription(rrule, start) {
  const rules = parseRRule(rrule);
  const interval = rules.INTERVAL || 1;

  let description = '';

  if (rules.FREQ === 'DAILY') {
    description = interval === 1 ? 'Daily' : `Every ${interval} days`;
  } else if (rules.FREQ === 'WEEKLY') {
    if (rules.BYDAY && rules.BYDAY.length > 0) {
      const dayNames = rules.BYDAY.map(day => WEEKDAY_NAMES[RRULE_DAYS.indexOf(day)]);
      const prefix = interval === 1 ? 'Weekly on' : `Every ${interval} weeks on`;
      description = `${prefix} ${dayNames.join(', ')}`;
    } else {
      description = interval === 1 ? 'Weekly' : `Every ${interval} weeks`;
    }
  } else if (rules.FREQ === 'MONTHLY') {
    const prefix = interval === 1 ? 'Monthly' : `Every ${interval} months`;

    if (rules.BYMONTHDAY) {
      const day = rules.BYMONTHDAY;
      const suffix = day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
      description = `${prefix} on the ${day}${suffix}`;
    } else if (rules.BYDAY && rules.BYSETPOS !== undefined) {
      const dayName = WEEKDAY_NAMES[RRULE_DAYS.indexOf(rules.BYDAY[0])];
      if (rules.BYSETPOS === -1) {
        description = `${prefix} on the last ${dayName}`;
      } else {
        const positions = ['', 'first', 'second', 'third', 'fourth', 'fifth'];
        description = `${prefix} on the ${positions[rules.BYSETPOS]} ${dayName}`;
      }
    }
  }

  if (rules.COUNT) {
    description += `, ${rules.COUNT} times`;
  } else if (rules.UNTIL) {
    const untilDate = new Date(rules.UNTIL);
    description += ` until ${untilDate.toLocaleDateString()}`;
  }

  return description;
}

/**
 * Get default monthly mode based on start date
 * @param {Date} startDate - Start date
 * @returns {Object} Default monthly settings
 */
export function getDefaultMonthlyMode(startDate) {
  const date = new Date(startDate);
  const dayOfMonth = date.getDate();
  const dayOfWeek = date.getDay();
  const weekOfMonth = Math.ceil(dayOfMonth / 7);

  // Check if this is the last occurrence of this weekday in the month
  const nextWeek = new Date(date);
  nextWeek.setDate(date.getDate() + 7);
  const isLastWeekday = nextWeek.getMonth() !== date.getMonth();

  return {
    sameDate: { day: dayOfMonth },
    sameWeekday: { day: dayOfWeek, week: weekOfMonth },
    lastWeekday: { day: dayOfWeek, isLast: isLastWeekday }
  };
}
